import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { estagioService } from '@/services/api';
import { minutosParaHorasMinutos } from '@/utils/timeConverter';
import { formatarTelefoneBr } from '@/utils/formatarTelefoneBr';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  ArrowPathIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

export const VerEstagioPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const [reativando, setReativando] = useState(false);

  const { data: estagio, isLoading, error } = useQuery({
    queryKey: ['estagio', id],
    queryFn: () => estagioService.getById(Number(id!)),
    enabled: !!id,
  });

  const reativarMutation = useMutation({
    mutationFn: () => estagioService.reativar(Number(id!)),
    onSuccess: () => {
      toast.success('Estágio reativado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['estagio', id] });
      queryClient.invalidateQueries({ queryKey: ['estagios'] });
      queryClient.invalidateQueries({ queryKey: ['estagios-rescisao-pdf'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      closeDialog();
      setReativando(false);
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string } } };
      toast.error(axiosError?.response?.data?.error || 'Erro ao reativar estágio');
      setReativando(false);
    },
  });

  const handleReativar = () => {
    showConfirm(
      'Reativar estágio?',
      'Isso desfaz o cancelamento/rescisão e volta o estágio para Ativo, como antes do encerramento. Um e-mail de notificação será enviado. Documentos de rescisão já gerados devem ser desconsiderados.',
      () => {
        setReativando(true);
        reativarMutation.mutate();
      },
      'warning'
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados do estágio...</p>
        </div>
      </div>
    );
  }

  if (error || !estagio) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-red-600">Erro ao carregar dados do estágio</p>
          <Button
            onClick={() => navigate('/estagios')}
            className="mt-4"
          >
            Voltar para Lista
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Não informado';
    const date = new Date(dateString);
    // Usar UTC para evitar problemas de timezone (dia a menos)
    const dia = date.getUTCDate().toString().padStart(2, '0');
    const mes = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const ano = date.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
  };

  const toMoneyNumber = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value === 'string') {
      const normalizedValue = value
        .trim()
        .replace(/\./g, '')
        .replace(',', '.');
      const parsedValue = Number(normalizedValue);
      return Number.isFinite(parsedValue) ? parsedValue : undefined;
    }

    return undefined;
  };

  const formatCurrency = (value: unknown) => {
    const numericValue = toMoneyNumber(value);
    if (numericValue === undefined) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numericValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'bg-green-100 text-green-800';
      case 'Concluido':
        return 'bg-blue-100 text-blue-800';
      case 'Cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'Obrigatorio':
        return 'bg-purple-100 text-purple-800';
      case 'NaoObrigatorio':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const bolsaAuxilio = toMoneyNumber(estagio.bolsaAuxilio) ?? 0;
  const auxilioTransporte = estagio.transporteFornecidoEmpresa ? 0 : (toMoneyNumber(estagio.auxilioTransporte) ?? 0);
  const valeAlimentacao = toMoneyNumber(estagio.valeAlimentacao) ?? 0;
  const totalBeneficios = bolsaAuxilio + auxilioTransporte + valeAlimentacao;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/estagios')}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          <Link to={`/estagios/${estagio.id}/editar`} className="flex-1 sm:flex-initial">
            <Button className="w-full sm:w-auto flex items-center gap-2 justify-center">
              <PencilIcon className="h-4 w-4" />
              <span>Editar</span>
            </Button>
          </Link>
          {estagio.status === 'Cancelado' && (
            <Button
              variant="outline"
              onClick={handleReativar}
              disabled={reativando || reativarMutation.isPending}
              className="flex-1 sm:flex-initial flex items-center gap-2 justify-center border-green-600 text-green-700 hover:bg-green-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span>Reativar</span>
            </Button>
          )}
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Estágio #{estagio.id}
          </h1>
          <p className="text-sm text-gray-500 truncate">
            {estagio.aluno?.nome} - {estagio.empresa?.nome}
          </p>
        </div>
      </div>

      {/* Status e Tipo */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(estagio.status)}`}>
          {estagio.status}
        </span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoColor(estagio.tipo)}`}>
          {estagio.tipo === 'Obrigatorio' ? 'Obrigatório' : 'Não Obrigatório'}
        </span>
        {estagio.remunerado && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Remunerado
          </span>
        )}
      </div>

      {/* Informações Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados do Estagiário */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <UserIcon className="h-5 w-5 flex-shrink-0" />
              <span>Dados do Estagiário</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Nome</Label>
              <p className="text-sm text-gray-900 break-words">{estagio.aluno?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">RG</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.rg || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">CPF</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.cpf || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Email</Label>
              <p className="text-sm text-gray-900 break-all">{estagio.aluno?.email || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Telefone</Label>
              <p className="text-sm text-gray-900">{formatarTelefoneBr(estagio.aluno?.telefone, 'Não informado')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Dados da Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <BuildingOfficeIcon className="h-5 w-5 flex-shrink-0" />
              <span>Dados da Empresa</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Razão Social</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Nome Fantasia</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.nomeFantasia || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">CNPJ</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.cnpj || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Representante</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.representante || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Email</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.email || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações Acadêmicas e Financeiras */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados Acadêmicos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <AcademicCapIcon className="h-5 w-5 flex-shrink-0" />
              <span>Dados Acadêmicos</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Instituição</Label>
              <p className="text-sm text-gray-900">{estagio.instituicao?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Curso</Label>
              <p className="text-sm text-gray-900">{estagio.curso?.nome || estagio.aluno?.curso?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Origem da Instituição</Label>
              <p className="text-sm text-gray-900">{estagio.origemInstituicao || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Agente Integrador</Label>
              <p className="text-sm text-gray-900">{estagio.agenteIntegrador?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Série/Período</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.serie || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Dados Financeiros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CurrencyDollarIcon className="h-5 w-5 flex-shrink-0" />
              <span>Dados Financeiros</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Bolsa Auxílio</Label>
              <p className="text-sm text-gray-900">{formatCurrency(estagio.bolsaAuxilio)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Auxílio-transporte</Label>
              <p className="text-sm text-gray-900">
                {estagio.transporteFornecidoEmpresa
                  ? 'Fornecido pela empresa'
                  : formatCurrency(estagio.auxilioTransporte)}
              </p>
            </div>
            {estagio.valeAlimentacao != null && Number(estagio.valeAlimentacao) > 0 && (
              <div>
                <Label className="text-sm font-medium text-gray-500">Vale alimentação</Label>
                <p className="text-sm text-gray-900">{formatCurrency(estagio.valeAlimentacao)}</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-gray-500">Total de benefícios</Label>
              <p className="text-sm text-gray-900">{formatCurrency(totalBeneficios)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Remunerado</Label>
              <p className="text-sm text-gray-900">{estagio.remunerado ? 'Sim' : 'Não'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Carga Horária Semanal</Label>
              <p className="text-sm text-gray-900">{estagio.cargaHorariaSemanal ? minutosParaHorasMinutos(estagio.cargaHorariaSemanal) : 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações de Período e Seguro */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Período do Estágio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CalendarIcon className="h-5 w-5 flex-shrink-0" />
              <span>Período do Estágio</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Início</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataInicio)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Término</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataTermino)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Assinatura</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataAssinatura)}</p>
            </div>
            {estagio.dataCancelamento && (
              <div>
                <Label className="text-sm font-medium text-gray-500">Data de Cancelamento</Label>
                <p className="text-sm text-gray-900">{formatDate(estagio.dataCancelamento)}</p>
              </div>
            )}
            {estagio.motivoEncerramento?.motivoPrincipal && (
              <div>
                <Label className="text-sm font-medium text-gray-500">Motivo do encerramento</Label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{estagio.motivoEncerramento.motivoPrincipal}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seguro */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <ShieldCheckIcon className="h-5 w-5 flex-shrink-0" />
              <span>Seguro</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Seguradora</Label>
              <p className="text-sm text-gray-900">{estagio.seguradora?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Apólice</Label>
              <p className="text-sm text-gray-900">{estagio.seguroApolice || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de emissão</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataEmissao)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Possui Responsável (Menor)</Label>
              <p className="text-sm text-gray-900">{estagio.possuiResponsavelMenor ? 'Sim' : 'Não'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações Adicionais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <DocumentTextIcon className="h-5 w-5 flex-shrink-0" />
            <span>Informações Adicionais</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Criação</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.createdAt)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Última Atualização</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Reativar"
        cancelText="Voltar"
        isLoading={reativando || reativarMutation.isPending}
      />
    </div>
  );
};
